import axios from 'axios';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import type { PlatformLoginResponse } from '@/types/platform-auth.types';

/**
 * Dedicated axios instance for refresh calls only.
 *
 * Intentionally has NO interceptors so it cannot trigger the 401 → refresh loop
 * and so the response envelope is read as-is (not auto-unwrapped).
 */
const refreshClient = axios.create({
  baseURL: '/api/v1',
  timeout: 10_000,
  withCredentials: true,
});

/**
 * In-flight refresh promise — multiple simultaneous callers (401 interceptor + proactive
 * timer) share one outgoing request and all receive the same resolved/rejected result.
 */
let activeRefresh: Promise<string> | null = null;

/**
 * Refreshes the platform access token using the stored refresh token.
 *
 * Returns the new access token on success.
 * Clears auth and throws on failure (expired / revoked refresh token).
 * Concurrent calls before the first settles share the same in-flight promise.
 */
export function refreshPlatformAccessToken(): Promise<string> {
  if (activeRefresh !== null) return activeRefresh;

  activeRefresh = performRefresh().finally(() => {
    activeRefresh = null;
  });

  return activeRefresh;
}

async function performRefresh(): Promise<string> {
  const { refreshToken, clearAuth, rotateTokens } = usePlatformAuthStore.getState();

  if (!refreshToken) {
    clearAuth();
    throw new Error('No refresh token available');
  }

  // Backend returns { data: PlatformLoginResponse, meta: {...} } — no interceptor unwraps here
  const resp = await refreshClient.post<{ data: PlatformLoginResponse }>(
    '/platform/auth/refresh',
    { refreshToken },
  );

  const tokens = resp.data.data;
  if (!tokens?.accessToken || !tokens?.refreshToken) {
    clearAuth();
    throw new Error('Malformed refresh response');
  }

  rotateTokens(tokens.accessToken, tokens.refreshToken);
  return tokens.accessToken;
}
